// ═══════════════════════════════════════════════════════════════════════════════
// API Client - Centralized fetch with timeout, retry, and error handling
// ═══════════════════════════════════════════════════════════════════════════════

interface FetchOptions {
  timeout?: number;
  retries?: number;
}

/**
 * Fetch with authentication, timeout, and retry logic
 * @param url - API endpoint URL
 * @param accessToken - Bearer token
 * @param options - timeout (ms) and retries count
 */
export async function fetchWithAuth<T>(
  url: string,
  accessToken: string,
  options: FetchOptions = {}
): Promise<T> {
  const { timeout = 10000, retries = 1 } = options;

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const errorMessage =
          body?.error?.message || body?.message || `API error: ${response.status}`;
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err instanceof Error ? err : new Error(String(err));

      if (err instanceof Error && err.name === 'AbortError') {
        lastError = new Error(`Timeout after ${timeout}ms`);
      }

      // Don't retry on 4xx errors (client errors)
      if (lastError.message.includes('API 4')) break;
    }
  }

  throw lastError;
}
